import { useState } from "react";
import {
  tokens,
  sectionLabelStyle,
  Card,
  CardHeader,
  Badge,
  Th,
  Td,
  StatusPill,
  FooterNote,
  PrimaryBtn,
  MetricCard,
  boldCellStyle,
} from "../dashboardKit";

// ============================================================
// Data
// ============================================================

const KPIS = [
  {
    badge: "QUERY",
    title: "Total AR",
    value: "AED 4.3M",
    sub: "Billed but not collected · Last month: AED 4.0M",
  },
  {
    badge: "QUERY",
    title: "DSO (Trailing 3 months)",
    value: "56 days",
    sub: "Days sales outstanding · Target: 45 days",
  },
  {
    badge: "AI",
    title: "High-risk AR",
    value: "AED 780,000",
    valueColor: tokens.dangerColor,
    sub: "Probability of default > 20% · 18% of total AR",
  },
];

const AR_AGING = [
  { bucket: "0–30 days", amount: "AED 2.1M", share: "49%", status: "On track" },
  {
    bucket: "31–60 days",
    amount: "AED 1.1M",
    share: "26%",
    status: "On track",
  },
  { bucket: "61–90 days", amount: "AED 650k", share: "15%", status: "Watch" },
  { bucket: "> 90 days", amount: "AED 450k", share: "10%", status: "Overdue" },
];

const TOP_CUSTOMERS = [
  {
    name: "RetailCo MENA",
    ar: "320,000",
    days: 74,
    risk: "High",
    next: "Escalate & revise terms",
  },
  {
    name: "GulfMart",
    ar: "210,000",
    days: 45,
    risk: "Medium",
    next: "Call & agree plan",
  },
  {
    name: "CityElectro",
    ar: "95,000",
    days: 28,
    risk: "Low",
    next: "Automated reminder",
  },
  {
    name: "DesertTrade",
    ar: "75,000",
    days: 61,
    risk: "Medium",
    next: "Send statement",
  },
];

const COLLECTIONS = [
  { day: "Day 1", opening: "3,789", closing: "3,789", breach: "No" },
  { day: "Day 10", opening: "3,789", closing: "3,789", breach: "No" },
  { day: "Day 20", opening: "3,789", closing: "3,789", breach: "No" },
  { day: "Day 27", opening: "3,789", closing: "3,789", breach: "Close" },
  { day: "Day 30", opening: "3,789", closing: "3,789", breach: "Close" },
];

const BANK_ACCOUNTS = [
  {
    account: "Operating Main",
    bank: "Emirates NBD",
    entity: "Holdings UAE",
    balance: "3,789",
    share: "55%",
  },
  {
    account: "Payroll",
    bank: "ADCB",
    entity: "Holdings UAE",
    balance: "3,789",
    share: "14%",
  },
  {
    account: "Collections",
    bank: "FAB",
    entity: "Trading LLC",
    balance: "3,789",
    share: "12%",
  },
  {
    account: "USD Export",
    bank: "HSBC",
    entity: "Export FZCO",
    balance: "3,789",
    share: "10%",
  },
];

const INITIAL_DISCOUNTS = [
  {
    vendor: "RetailCo MENA",
    invoice: "#RC-2201",
    due: "05 Jan",
    discount: "2% if paid by 20 Dec",
    decision: "Accept",
  },
  {
    vendor: "GulfMart",
    invoice: "#GM-8821",
    due: "12 Jan",
    discount: "1.5% if paid by 22 Dec",
    decision: "Review",
  },
  {
    vendor: "CityElectro",
    invoice: "#CE-3342",
    due: "18 Dec",
    discount: "1% if paid by 10 Dec",
    decision: "Accept",
  },
];

const gridTwo = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
  gap: 16,
  marginBottom: 16,
};

// ============================================================
// Component
// ============================================================

export default function Receivables() {
  const [discounts, setDiscounts] = useState(INITIAL_DISCOUNTS);

  function cycleDecision(idx) {
    setDiscounts((prev) =>
      prev.map((row, i) => {
        if (i !== idx) return row;
        const order = ["Accept", "Review", "Declined"];
        const next = order[(order.indexOf(row.decision) + 1) % order.length];
        return { ...row, decision: next };
      }),
    );
  }

  return (
    <div className="py-1">

      <div
        style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 24 }}
      >
        {KPIS.map((k) => (
          <MetricCard
            key={k.title}
            badge={k.badge}
            title={k.title}
            value={k.value}
            valueColor={k.valueColor}
            sub={k.sub}
          />
        ))}
      </div>

      {/* ── Working capital detail ── */}
      <div style={sectionLabelStyle}>Working capital detail</div>
      <div style={gridTwo}>
        {/* AR Aging Detail */}
        <Card>
          <CardHeader
            title="AR aging detail"
            sub="Distribution by bucket"
            badge={<Badge type="QUERY" />}
          />
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <Th>Bucket</Th>
                <Th>Amount</Th>
                <Th>Share</Th>
                <Th>Status</Th>
              </tr>
            </thead>
            <tbody>
              {AR_AGING.map((row) => (
                <tr key={row.bucket}>
                  <Td>{row.bucket}</Td>
                  <Td style={boldCellStyle(row.status === "Overdue")}>
                    {row.amount}
                  </Td>
                  <Td style={{ color: tokens.mutedColor }}>{row.share}</Td>
                  <Td>
                    <StatusPill level={row.status} />
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
          <FooterNote>
            <span>
              AI focus: 61–90 and &gt; 90 day buckets for proactive collections.
            </span>
          </FooterNote>
        </Card>

        {/* Top Overdue Customers */}
        <Card>
          <CardHeader
            title="Top overdue customers"
            sub="AI-prioritised · example top-4 list"
            badge={<Badge type="AI" />}
          />
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <Th>Customer</Th>
                <Th>AR</Th>
                <Th>Days</Th>
                <Th>Risk</Th>
                <Th>Next step</Th>
              </tr>
            </thead>
            <tbody>
              {TOP_CUSTOMERS.map((row) => (
                <tr key={row.name}>
                  <Td style={boldCellStyle()}>{row.name}</Td>
                  <Td>{row.ar}</Td>
                  <Td>{row.days}</Td>
                  <Td>
                    <StatusPill level={row.risk} />
                  </Td>
                  <Td style={{ color: tokens.mutedColor, fontSize: 12 }}>
                    {row.next}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
          <FooterNote>
            <span>Prioritize top 10 accounts to reduce DSO by 7–10 days.</span>
            <PrimaryBtn>Generate call list</PrimaryBtn>
          </FooterNote>
        </Card>
      </div>

      <div style={gridTwo}>
        {/* Collections Performance */}
        <Card>
          <CardHeader
            title="Collections performance"
            sub="Last 3 months"
            badge={<Badge type="QUERY" />}
          />
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <Th>Day</Th>
                <Th>Opening</Th>
                <Th>Closing</Th>
                <Th>Floor breach?</Th>
              </tr>
            </thead>
            <tbody>
              {COLLECTIONS.map((row) => (
                <tr key={row.day}>
                  <Td>{row.day}</Td>
                  <Td style={boldCellStyle()}>
                    {row.opening}
                    <span style={{ color: "#9CA3AF" }}>.00</span>
                  </Td>
                  <Td style={boldCellStyle()}>
                    {row.closing}
                    <span style={{ color: "#9CA3AF" }}>.00</span>
                  </Td>
                  <Td>
                    <StatusPill level={row.breach} />
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>

        {/* Disputes & Credit Holds */}
        <Card>
          <CardHeader
            title="Disputes & credit holds"
            sub="Example pipeline"
            badge={<Badge type="AI" />}
          />
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <Th>Account</Th>
                <Th>Bank</Th>
                <Th>Entity</Th>
                <Th>Balance</Th>
                <Th>Share</Th>
              </tr>
            </thead>
            <tbody>
              {BANK_ACCOUNTS.map((row) => (
                <tr key={row.account}>
                  <Td style={boldCellStyle()}>{row.account}</Td>
                  <Td style={{ color: tokens.mutedColor }}>{row.bank}</Td>
                  <Td style={{ color: tokens.mutedColor }}>{row.entity}</Td>
                  <Td style={boldCellStyle()}>
                    {row.balance}
                    <span style={{ color: "#9CA3AF" }}>.00</span>
                  </Td>
                  <Td style={{ color: tokens.mutedColor }}>{row.share}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </div>

      {/* Discount Opportunities */}
      <div style={gridTwo}>
        <Card>
          <CardHeader
            title="Discount opportunities"
            sub="Accelerate cash inflow with intelligent early payment incentives"
            badge={<Badge type="AI" />}
          />
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <Th>Vendor</Th>
                <Th>Invoice</Th>
                <Th>Due</Th>
                <Th>Discount</Th>
                <Th>Decision</Th>
              </tr>
            </thead>
            <tbody>
              {discounts.map((row, idx) => (
                <tr key={row.invoice}>
                  <Td style={boldCellStyle()}>{row.vendor}</Td>
                  <Td style={{ color: tokens.mutedColor }}>{row.invoice}</Td>
                  <Td style={{ color: tokens.mutedColor }}>{row.due}</Td>
                  <Td style={{ color: tokens.mutedColor }}>{row.discount}</Td>
                  <Td>
                    <StatusPill
                      level={row.decision}
                      onClick={() => cycleDecision(idx)}
                    />
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
          <FooterNote>
            <span>Align discount acceptance with cashflow scenarios.</span>
            <PrimaryBtn>Simulate impact</PrimaryBtn>
          </FooterNote>
        </Card>
        <div style={{ minWidth: 300 }} />
      </div>
    </div>
  );
}
